/**
 * Datei → Dokument/Bild-Konvertierung fürs Hochladen (client.md §10). Gemeinsam
 * genutzt von Wizard.tsx (Erstbefüllung) und tabs/Files.tsx (Nachbefüllung), damit
 * beide Stellen exakt dieselbe xlsx/pdf/Bild-Behandlung und Größengrenze verwenden.
 */
import type { AddDocument, AddImage } from "../api/types";
import { xlsxToCsvDocuments } from "../convert/xlsx";
import { pdfToText, pdfToPngPages } from "../convert/pdf";
import { LARGE_FILE_THRESHOLD } from "./upload";

export function convLabelFor(file: File, asImages: boolean): string {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "xlsx") return "CSV";
  if (ext === "pdf") return asImages ? "Bildseiten" : "Text";
  if (["png", "jpg", "jpeg", "webp"].includes(ext)) return "Bild";
  if (file.size > LARGE_FILE_THRESHOLD) return "Datei-Speicher";
  return "Text";
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1] ?? "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export type ConvertedFiles = { smallDocs: AddDocument[]; smallImages: AddImage[]; largeFiles: File[] };

export async function convertFilesForUpload(files: File[], pdfAsImages: boolean): Promise<ConvertedFiles> {
  const smallDocs: AddDocument[] = [];
  const smallImages: AddImage[] = [];
  const largeFiles: File[] = [];

  for (const file of files) {
    if (file.size > LARGE_FILE_THRESHOLD) {
      largeFiles.push(file);
      continue;
    }
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    if (ext === "xlsx") {
      const docs = await xlsxToCsvDocuments(file);
      smallDocs.push(...docs);
    } else if (ext === "pdf") {
      if (pdfAsImages) {
        const pages = await pdfToPngPages(file);
        for (const p of pages) {
          const b64 = await blobToBase64(p.blob);
          smallImages.push({ doc_id: p.doc_id, content_base64: b64 });
        }
      } else {
        const text = await pdfToText(file);
        smallDocs.push({ doc_id: file.name.replace(/\.pdf$/i, ""), content: text });
      }
    } else if (["png", "jpg", "jpeg", "webp"].includes(ext)) {
      const b64 = await blobToBase64(file);
      smallImages.push({ doc_id: file.name, content_base64: b64 });
    } else {
      const text = await file.text();
      smallDocs.push({ doc_id: file.name, content: text });
    }
  }

  return { smallDocs, smallImages, largeFiles };
}

export function normalizeUmlauts(str: string): string {
  return str
    .normalize("NFC")
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/Ä/g, "Ae")
    .replace(/Ö/g, "Oe")
    .replace(/Ü/g, "Ue")
    .replace(/ß/g, "ss");
}